import { STEP_UP_OAUTH_CONFIG } from './constants';
import { StepUpFailedError } from './stepUpError';
import type { StepUpOAuthConfig } from './types';

export type CodeExchangeConfig = Pick<
  StepUpOAuthConfig,
  'tokenUrl' | 'redirectUri' | 'clientId'
>;

export const DEFAULT_CODE_EXCHANGE_CONFIG: CodeExchangeConfig = {
  tokenUrl: STEP_UP_OAUTH_CONFIG.TOKEN_URL,
  redirectUri: STEP_UP_OAUTH_CONFIG.REDIRECT_URI,
  clientId: STEP_UP_OAUTH_CONFIG.CLIENT_ID,
};

interface TokenResponse {
  access_token?: string;
}

// Exchanges the OAuth authorization code (from the web_message popup) for an
// access token via oauth2ng's token endpoint (authorization_code grant + PKCE).
// Sends the same `redirect_uri` used at /authorize and the PKCE `code_verifier`,
// per Dalia. `credentials: 'include'` so the session cookie rides along for the
// session-bound token. Rejections are platform-shaped StepUpFailedError so they
// flow through unified error handling.
export const exchangeCodeForToken = async (
  code: string,
  codeVerifier: string,
  config: CodeExchangeConfig = DEFAULT_CODE_EXCHANGE_CONFIG,
): Promise<string> => {
  if (!config.tokenUrl) {
    throw new StepUpFailedError(
      undefined,
      'Step-up token endpoint is not configured (tokenUrl pending)',
    );
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    client_id: config.clientId,
    // Must be the SAME redirect_uri sent to /authorize — defaults to the current
    // origin, overridable via config.
    redirect_uri: config.redirectUri || window.location.origin,
    code_verifier: codeVerifier,
  });

  let response: Response;
  try {
    response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      credentials: 'include',
    });
  } catch (error) {
    throw new StepUpFailedError(
      undefined,
      `Step-up token exchange request failed: ${(error as Error).message}`,
    );
  }

  if (!response.ok) {
    throw new StepUpFailedError(
      undefined,
      `Step-up token exchange failed (HTTP ${response.status})`,
    );
  }

  const json = (await response.json()) as TokenResponse;
  if (!json.access_token) {
    throw new StepUpFailedError(
      undefined,
      'Step-up token exchange returned no access_token',
    );
  }
  return json.access_token;
};
